
class AccordionTabs {
  /**
   * AccordionTabs constructor
   * 
   * @param {string|HTMLElement} el - Accordion HTML element or CSS selector
   * @param {object} options - Optional settings
   * @param {string} [options.breakpoint=(min-width: 1200px)] - CSS media query to watch desktop breakpoint
   * @param {boolean} [options.allowMultiple=false] - Allow multiple panels to be opened at the same time (mobile only!)
   * @param {boolean} [options.allowNone=true] - Allow all panels to be closed at the same time (mobile only!)
   * @param {null|function} [options.onInit=null] - Callback function after initialization
   * @param {null|function} [options.onModeChange=null] - Callback function when desktop/mobile mode change happens
   */
  constructor(el, options = {}) {
    const _defaults = {
      breakpoint: '(min-width: 1200px)',
      allowMultiple: false,
      allowNone: true,
      onInit: null,
      onModeChange: null,
    };
    this.opts = { ..._defaults, ...options };
    
    this.el = (typeof el === 'string') ? document.querySelector(el) : el;
    this.headerEls = this.el.querySelectorAll('[aria-controls]');
    this.panelEls = this.el.querySelectorAll('[role="region"]');

    // this._watchBreakpoint();
    CMS.DataStore.subscribe('accordion_tabs/breakpoint', ({ match }) => {
      
      // set mode
      if (match) {
        this.mode = 'desktop';
      } else {
        this.mode = 'mobile';
      }

      // check callback
      if (typeof this.opts.onModeChange === 'function') {
        this.opts.onModeChange.call(null, this, this.mode);
      }

    });
    CMS.watchBreakpoint(this.opts.breakpoint, 'accordion_tabs/breakpoint', CMS.DataStore);

    this.boundClick = e => this._onClick(e);

    this.init();
  }

  /**
   * Init AccordionTabs events
   * 
   * @returns {void}
   */
  init() {
    Array.from(this.headerEls).forEach(headerEl => {
      headerEl.addEventListener('click', this.boundClick, false);
    });
    if (typeof this.opts.onInit === 'function') {
      this.opts.onInit.call(null, this, this.mode);
    }
  }

  /**
   * Destroy AccordionTabs events
   * 
   * @returns {void}
   */
  destroy() {
    Array.from(this.headerEls).forEach(headerEl => {
      headerEl.removeEventListener('click', this.boundClick);
    });
  }

  switchTo(tabId) {
    this._deactivatePanels();
    if (typeof tabId === 'number') {
      // index
      this._activatePanel(this.panelEls[tabId], this.headerEls[tabId]);
    } else if (typeof tabId === 'string') {
      // tab ID
      const headerEl = Array.from(this.headerEls).find(el => el.getAttribute('aria-controls') === tabId);
      const panelEl = Array.from(this.panelEls).find(el => el.getAttribute('id') === tabId);
      this._activatePanel(panelEl, headerEl);
    }
  }

  closeAll() {
    if (this.opts.allowNone) {
      this._deactivatePanels();
    }
  }

  /**
   * Handle header clicks
   * 
   * @param {MouseEvent|TouchEvent|KeyboardEvent} e - JavaScript event
   * 
   * @returns {void}
   */
  _onClick(e) {
    e.preventDefault();
    const isActive = e.target.getAttribute('aria-expanded').toLowerCase() === 'true';
    const panelEl = this.el.querySelector(`#${e.target.getAttribute('aria-controls')}`);
    
    if (this.mode === 'desktop') {
      if (!isActive) {
        this._deactivatePanels();
        this._activatePanel(panelEl, e.target);
      }
    } else if (this.mode === 'mobile') {
      if (isActive) {
        // deactivate
        if (
          this.opts.allowNone ||
          (!this.opts.allowNone && this._countActive() > 1)
        ) {
          this._deactivatePanel(panelEl, e.target);
        }
      } else {
        // activate
        if (!this.opts.allowMultiple) {
          this._deactivatePanels();
        }
        this._activatePanel(panelEl, e.target);
      }
    }

    // this._deactivatePanels();
    // this._activatePanel(panelEl, e.target);
  }

  _activatePanel(panelEl, headerEl) {
    panelEl.removeAttribute('hidden');
    headerEl.setAttribute('aria-expanded', 'true');
  }

  _deactivatePanel(panelEl, headerEl) {
    panelEl.setAttribute('hidden', 'true');
    headerEl.setAttribute('aria-expanded', 'false');
  }

  _deactivatePanels() {
    Array.from(this.panelEls).forEach(panelEl => panelEl.setAttribute('hidden', ''));
    Array.from(this.headerEls).forEach(headerEl => headerEl.setAttribute('aria-expanded', 'false'));
  }

  _countActive() {
    return Array.from(this.headerEls).filter(headerEl => headerEl.getAttribute('aria-expanded').toLowerCase() === 'true').length;
  }
}

export default AccordionTabs;
